import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import { blogAPI, BlogPostDetail, recordBlogEvent } from '../services/blog';
import Logo from '../components/Logo';
import './Blog.css';

// Single-author blog — set to '' to hide the link entirely.
const AUTHOR_LINKEDIN_URL = 'https://www.linkedin.com/in/yberhanu/';

const BlogPost: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const [post, setPost] = useState<BlogPostDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    blogAPI
      .getBySlug(slug)
      .then(p => {
        if (cancelled) return;
        setPost(p);

        // Set page title and meta tags for social sharing
        document.title = `${p.title} — Lomi 🍋`;
        setMeta('description', p.excerpt || '');
        setMeta('og:title', p.title, true);
        setMeta('og:description', p.excerpt || '', true);
        setMeta('og:type', 'article', true);
        setMeta('og:url', window.location.href, true);
        if (p.cover_image_url) {
          setMeta('og:image', p.cover_image_url, true);
        }
        setMeta('twitter:card', p.cover_image_url ? 'summary_large_image' : 'summary');
        setMeta('twitter:title', p.title);
        setMeta('twitter:description', p.excerpt || '');
        if (p.cover_image_url) {
          setMeta('twitter:image', p.cover_image_url);
        }
      })
      .catch(e => {
        if (cancelled) return;
        setError(e.message === 'NOT_FOUND' ? 'Post not found.' : (e.message || 'Failed to load'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Engagement tracking. 'open' fires once the post resolves; 'read' fires only
  // when someone has passed ~50% of the article AND stayed 30s+ — the pair is
  // what separates a reader from a bounce. Both are best-effort beacons.
  useEffect(() => {
    if (!post || !slug) return;

    recordBlogEvent(slug, 'open');

    const startedAt = Date.now();
    let counted = false;
    const DWELL_MS = 30_000;
    const DEPTH = 0.5;

    const maybeCount = () => {
      if (counted) return;
      const scrollable = document.body.scrollHeight - window.innerHeight;
      const depth = scrollable > 0 ? window.scrollY / scrollable : 1;
      if (depth >= DEPTH && Date.now() - startedAt >= DWELL_MS) {
        counted = true;
        recordBlogEvent(slug, 'read', { dwell_ms: Date.now() - startedAt });
        window.removeEventListener('scroll', maybeCount);
      }
    };

    window.addEventListener('scroll', maybeCount, { passive: true });
    const timer = window.setInterval(maybeCount, 5_000);

    return () => {
      window.removeEventListener('scroll', maybeCount);
      window.clearInterval(timer);
    };
  }, [post, slug]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const shareOn = (platform: 'twitter' | 'linkedin' | 'whatsapp') => {
    if (!post) return;
    recordBlogEvent(post.slug, 'share', { platform });
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(post.title);
    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
    };
    window.open(links[platform], '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="blog-page">
        <header className="blog-header">
          <button onClick={() => navigate('/blog')} className="blog-back-button">← Blog</button>
          <div className="blog-brand" onClick={() => navigate('/')}>
            <Logo height={30} />
          </div>
        </header>
        <main className="blog-content">
          <div className="blog-status">Loading…</div>
        </main>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-page">
        <header className="blog-header">
          <button onClick={() => navigate('/blog')} className="blog-back-button">← Blog</button>
          <div className="blog-brand" onClick={() => navigate('/')}>
            <Logo height={30} />
          </div>
        </header>
        <main className="blog-content">
          <div className="blog-status blog-status-error">
            {error || 'Post not found.'}
          </div>
          <div style={{ textAlign: 'center', marginTop: '1rem' }}>
            <button className="blog-back-button" onClick={() => navigate('/blog')}>
              ← Back to all posts
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <header className="blog-header">
        <button onClick={() => navigate('/blog')} className="blog-back-button" aria-label="Back to blog">
          ← Blog
        </button>
        <div className="blog-brand" onClick={() => navigate('/')}>
          <Logo height={30} />
        </div>
      </header>

      <main className="blog-content blog-post-content">
        <article>
          <header className="blog-post-header">
            <div className="blog-post-meta">
              {formatDate(post.published_at)} · {post.reading_time_min} min read
            </div>
            <h1>{post.title}</h1>
            {post.excerpt && (
              <p className="blog-post-excerpt">{post.excerpt}</p>
            )}
            <div className="blog-post-author">
              {post.author_avatar_url && (
                <img
                  src={post.author_avatar_url}
                  alt={post.author_name}
                  className="blog-post-author-avatar"
                />
              )}
              <div>
                <div className="blog-post-author-name">
                  {post.author_name}
                  {AUTHOR_LINKEDIN_URL && (
                    <>
                      {' '}
                      <a
                        className="blog-post-author-link"
                        href={AUTHOR_LINKEDIN_URL}
                        target="_blank"
                        rel="noopener noreferrer me"
                        aria-label={`${post.author_name} on LinkedIn`}
                      >
                        LinkedIn
                      </a>
                    </>
                  )}
                </div>
                {post.author_bio && (
                  <div className="blog-post-author-bio">{post.author_bio}</div>
                )}
              </div>
            </div>
          </header>

          {post.cover_image_url && (
            <img
              src={post.cover_image_url}
              alt={post.title}
              className="blog-post-cover"
            />
          )}

          <div className="blog-post-body">
            <MarkdownRenderer>{post.content_md}</MarkdownRenderer>
          </div>

          <div className="blog-post-tags">
            {(post.tags || []).map(t => (
              <span
                key={t}
                className="blog-card-tag"
                onClick={() => navigate(`/blog?tag=${encodeURIComponent(t)}`)}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="blog-share">
            <span>Share:</span>
            <button onClick={() => shareOn('twitter')} aria-label="Share on Twitter">𝕏</button>
            <button onClick={() => shareOn('linkedin')} aria-label="Share on LinkedIn">in</button>
            <button onClick={() => shareOn('whatsapp')} aria-label="Share on WhatsApp">WA</button>
          </div>
        </article>
      </main>

      <footer className="blog-footer">
        <p><Logo height={22} /> · Built in Ethiopia</p>
      </footer>
    </div>
  );
};

// Helper: set or update a <meta> tag
function setMeta(name: string, content: string, isProperty = false) {
  if (!content) return;
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

export default BlogPost;
